import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Avatar from 'boring-avatars';
import { Info, Plus, X, LinkSimple, Trash, Scales } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTopTraders } from '@/hooks/useDashboardData';
import { traderPath, copyTraderPath, traderLinkState } from '@/lib/traderSlug';
import { TRADER_COLUMNS, usd, formatROI } from '@/lib/traderColumns';
import { fmtPct } from '@/lib/format';
import {
  BETTER, COMPARE_GROUPS, COMPARE_MAX, bestIndexes,
  writeCompareList, removeFromCompare, useCompareNames, addToCompare, useVsLink,
} from '@/lib/compareTraders';

/****************
 * TRADER VS TRADER. 2026-09-26, modelled on MEXC's "Trader Performance
 * Comparison" (see lib/compareTraders.ts for what is kept and what is not).
 *
 * A pop-up, like the trade page, opened by the VS button on a trader's page
 * (Guillaume: "I would like the comparator to be a pop-up page a bit like the
 * Trade page is accessible from a VS button in the Trader's page").
 *
 * One column per trader, every metric of the Top Traders table as a row,
 * grouped. The window is the leaderboard's (All time / Month / Week), and the
 * numbers are the leaderboard's own, so a trader reads the same here as there.
 ****************/

const COLORS = ['#2563eb', '#10b981', '#64748b', '#0ea5e9', '#8b5cf6'];
const PERIODS = [['all', 'compare.period.all'], ['1m', 'compare.period.month'], ['1w', 'compare.period.week']] as const;

/** The VS pop-up. */
export function CompareDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation('traders');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto p-0 bg-background">
        <DialogHeader className="px-6 py-4 border-b border-border/50">
          <DialogTitle className="flex items-center gap-2"><Scales size={20} /> {t('compare.title')}</DialogTitle>
          <DialogDescription>{t('compare.description')}</DialogDescription>
        </DialogHeader>
        <div className="p-6">
          <ComparePanel onLeave={() => onOpenChange(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The comparison itself: exported apart from the pop-up so it can be tested. */
export function ComparePanel({ onLeave }: { onLeave?: () => void }) {
  const { t: tr } = useTranslation('traders');
  const navigate = useNavigate();
  const names = useCompareNames();
  const [period, setPeriod] = useState<string>('all');
  const [highlight, setHighlight] = useState(true);
  const [picking, setPicking] = useState(false);

  // The Dashboard's own list (same key, so usually already cached). Asking for
  // the below-floor traders too made it a different request that nothing had
  // warmed: 18-32 s cold on production, and the pop-up sat on "Loading"
  // (Guillaume, 2026-09-26: "the list is loading infinitely").
  const { data: roster, isLoading, isError, refetch } = useTopTraders(0, period);
  const byName = useMemo(() => {
    const m = new Map<string, any>();
    for (const t of roster || []) {
      const n = t.traders?.trader_name;
      if (n) m.set(n, t);
    }
    return m;
  }, [roster]);

  const columns = names.map((n) => ({ name: n, t: byName.get(n) }));

  const share = async () => {
    // The names as they are (2026-10-01: an address carries the trader's name).
    // useVsLink still reads the "~" slugs in links shared before.
    const url = `${window.location.origin}${window.location.pathname}?vs=${names.map((n) => encodeURIComponent(n)).join(',')}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(tr('compare.linkCopied'));
    } catch {
      toast.error(tr('compare.copyFailed'));
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {names.length === 0
          ? tr('compare.pickUpTo', { max: COMPARE_MAX })
          : tr('compare.countOfMax', { count: names.length, max: COMPARE_MAX })}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/30 border border-border/30">
          {PERIODS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setPeriod(value)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                period === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tr(label)}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={highlight} onCheckedChange={setHighlight} />
          {tr('compare.highlight')}
          <Tooltip>
            <TooltipTrigger asChild><Info size={14} className="cursor-help" /></TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {tr('compare.highlight.info')}
            </TooltipContent>
          </Tooltip>
        </label>
        <div className="ms-auto flex items-center gap-2">
          <Button size="sm" onClick={() => setPicking(true)} disabled={names.length >= COMPARE_MAX}>
            <Plus size={14} className="me-1" /> {tr('compare.addTrader')}
          </Button>
          {names.length > 0 && (
            <>
              <Button size="sm" variant="outline" onClick={share}><LinkSimple size={14} className="me-1" /> {tr('compare.share')}</Button>
              <Button size="sm" variant="outline" onClick={() => writeCompareList([])}><Trash size={14} className="me-1" /> {tr('compare.clearAll')}</Button>
            </>
          )}
        </div>
      </div>

      {names.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="py-12 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              {tr('compare.empty')}
            </p>
            <Button onClick={() => setPicking(true)}><Plus size={14} className="me-1" /> {tr('compare.addTrader')}</Button>
          </CardContent>
        </Card>
      ) : isError && !(roster && roster.length) ? (
        <Card className="glass-card">
          <CardContent className="py-10 text-center space-y-3">
            <p className="text-sm text-muted-foreground">{tr('compare.numbersFailed')}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>{tr('compare.tryAgain')}</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="glass-card overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-8rem)]">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 [&_th]:bg-card">
                <tr>
                  <th className="sticky start-0 z-10 bg-card text-start p-3 min-w-[180px] border-b border-border/40" />
                  {columns.map(({ name, t }) => (
                    <th key={name} className="p-3 min-w-[160px] border-b border-border/40 align-top">
                      <div className="flex flex-col items-center gap-1.5 relative">
                        <button
                          type="button"
                          aria-label={tr('compare.remove', { name })}
                          onClick={() => removeFromCompare(name)}
                          className="absolute -top-1 -end-1 text-muted-foreground hover:text-foreground"
                        >
                          <X size={14} />
                        </button>
                        <Avatar size={36} name={name} variant="beam" colors={COLORS} />
                        <Link to={traderPath(name, t?.trader_id)} state={traderLinkState(t?.trader_id)} onClick={() => onLeave?.()} className="font-semibold hover:underline truncate max-w-[140px]">
                          {name}
                        </Link>
                        {t?.below_floor && <Badge variant="destructive" className="text-[10px]">{tr('compare.heavyLoss')}</Badge>}
                        {t ? (
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => { onLeave?.(); navigate(copyTraderPath(name, t.trader_id), { state: traderLinkState(t.trader_id) }); }}>
                            {tr('compare.copy')}
                          </Button>
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_GROUPS.map((g) => (
                  <React.Fragment key={g.title}>
                    <tr>
                      <td colSpan={columns.length + 1} className="sticky start-0 bg-muted/20 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {tr(g.title)}
                      </td>
                    </tr>
                    {g.keys.map((key) => {
                      const col = TRADER_COLUMNS.find((c) => c.key === key);
                      if (!col) return null;
                      const values = columns.map(({ t }) => (t ? col.get(t) : null));
                      const best = highlight ? new Set(bestIndexes(values, BETTER[key] ?? null)) : new Set<number>();
                      return (
                        <tr key={key} className="border-b border-border/20">
                          <td className="sticky start-0 z-10 bg-card px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              {tr(col.label)}
                              {col.info && (
                                <Tooltip>
                                  <TooltipTrigger asChild><Info size={12} className="cursor-help opacity-60" /></TooltipTrigger>
                                  <TooltipContent className="max-w-xs">{tr(col.info)}</TooltipContent>
                                </Tooltip>
                              )}
                            </span>
                          </td>
                          {columns.map(({ name, t }, i) => (
                            <td
                              key={name}
                              className={`px-3 py-2.5 text-center font-mono text-xs ${best.has(i) ? 'bg-primary/15 font-semibold ring-1 ring-inset ring-primary/40' : ''}`}
                            >
                              {isLoading && !t ? '…' : t ? col.render(t) : '—'}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {columns.some(({ t }) => !t) && !isLoading && (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {tr('compare.dashNote')}
            </p>
          )}
        </Card>
      )}

      <AddTraders
        open={picking}
        onClose={() => setPicking(false)}
        roster={roster || []}
        loading={isLoading}
        failed={isError}
        retry={() => refetch()}
        chosen={names}
      />
    </div>
  );
}

function AddTraders({ open, onClose, roster, chosen, loading, failed, retry }: {
  open: boolean; onClose: () => void; roster: any[]; chosen: string[];
  loading: boolean; failed: boolean; retry: () => void;
}) {
  const { t: tr } = useTranslation('traders');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  useEffect(() => { if (open) { setPicked([]); setQ(''); } }, [open]);

  const room = COMPARE_MAX - chosen.length;
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return roster
      .filter((t) => t.traders?.trader_name && !chosen.includes(t.traders.trader_name))
      .filter((t) => !needle || t.traders.trader_name.toLowerCase().includes(needle))
      .sort((a, b) => (b.total_pnl_usd || 0) - (a.total_pnl_usd || 0))
      .slice(0, 100);
  }, [roster, chosen, q]);

  const toggle = (name: string) => {
    setPicked((p) => {
      if (p.includes(name)) return p.filter((x) => x !== name);
      if (p.length >= room) {
        toast.error(tr('compare.upTo', { max: COMPARE_MAX }));
        return p;
      }
      return [...p, name];
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{tr('compare.add.title')}</DialogTitle>
          <DialogDescription>
            {room > 0 ? tr('compare.add.room', { room }) : tr('compare.add.full', { max: COMPARE_MAX })}
          </DialogDescription>
        </DialogHeader>
        <Input placeholder={tr('compare.add.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="max-h-[50vh] overflow-y-auto divide-y divide-border/30">
          {/* Three different states, never one word for all of them: a list
              that failed must not say "Loading" for ever. */}
          {loading && roster.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{tr('compare.add.loading')}</p>
          ) : failed && roster.length === 0 ? (
            <div className="py-6 text-center space-y-2">
              <p className="text-sm text-muted-foreground">{tr('compare.add.failed')}</p>
              <Button size="sm" variant="outline" onClick={retry}>{tr('compare.tryAgain')}</Button>
            </div>
          ) : list.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {q.trim() ? tr('compare.add.noMatch') : tr('compare.add.allIn')}
            </p>
          ) : list.map((t) => {
            const name = t.traders.trader_name;
            return (
              <label key={name} className="flex items-center gap-3 py-2 cursor-pointer">
                <Checkbox checked={picked.includes(name)} onCheckedChange={() => toggle(name)} />
                <Avatar size={24} name={name} variant="beam" colors={COLORS} />
                <span className="flex-1 truncate text-sm">{name}</span>
                <span className={`text-xs font-mono ${(t.total_pnl_usd || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                  {usd(t.total_pnl_usd || 0)}
                </span>
                <span className="text-xs font-mono text-muted-foreground w-16 text-end">
                  {formatROI(t.roi_percent || 0, t.total_pnl_usd || 0)}
                </span>
                <span className="text-xs font-mono text-muted-foreground w-12 text-end">{fmtPct(t.win_rate || 0, 0)}</span>
              </label>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{tr('compare.add.cancel')}</Button>
          <Button
            disabled={picked.length === 0}
            onClick={() => { writeCompareList([...chosen, ...picked]); onClose(); }}
          >
            {picked.length ? tr('compare.add.confirm', { count: picked.length }) : tr('compare.add.confirmNone')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/****************
 * THE VS BUTTON AND ITS POP-UP, SELF-CONTAINED. 2026-09-26.
 *
 * Guillaume: "The vs to compare trader is very slow to render". The open/close
 * state lived in the trader page and the Dashboard, so opening the pop-up
 * re-rendered the whole page behind it, charts and tables included. Here the
 * state is local: opening it renders the pop-up and nothing else. A shared
 * ?vs= link is picked up here too.
 *
 * `addName`: the trader to add when clicked (the trader page). `children`: the
 * button's content, so each host keeps its own look.
 ****************/
export function VsLauncher({ addName, className, size, title, children, bare = false }: {
  addName?: string; className?: string; size?: 'sm' | 'default'; title?: string; children: React.ReactNode;
  /** No box: an icon among the profile's icons (round 8 #217). */
  bare?: boolean;
}) {
  const { t } = useTranslation('traders');
  const [open, setOpen] = useState(false);
  useVsLink(() => setOpen(true));
  return (
    <>
      <Button
        variant={bare ? 'ghost' : 'outline'}
        size={size}
        className={className}
        title={title}
        aria-label={title}
        onClick={() => {
          if (addName && !addToCompare(addName)) toast.error(t('compare.upTo', { max: COMPARE_MAX }));
          setOpen(true);
        }}
      >
        {children}
      </Button>
      <CompareDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

